import { useState, useMemo } from 'react';

interface MonthData {
  month: number;
  monthKo: string;
  overall: number;
  money: number;
  love: number;
  health: number;
  career: number;
  social: number;
  advice: string;
  lucky: string;
}

interface MonthlyFortuneCalendarProps {
  birthDate: string;
  birthTime: string;
  gender: 'male' | 'female';
}

// 선형 합동 생성기 기반 결정론적 난수
function seededRand(seed: number): () => number {
  let s = (seed ^ 0x12345678) >>> 0;
  return () => {
    s = Math.imul(s, 1664525) + 1013904223;
    s = s >>> 0;
    return s / 0xffffffff;
  };
}

const MONTHS_KO = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

const MONTH_ADVICES: string[][] = [
  ['새해 기운이 상승하는 시기. 새로운 시작에 집중하세요.', '계획을 꼼꼼히 세우는 달. 무리한 투자는 자제하세요.', '변화의 기운이 강해집니다. 유연한 대처가 필요합니다.'],
  ['대인관계가 활발해지는 시기. 새 인연을 주목하세요.', '안정을 추구하는 달. 내실을 다지는 것이 좋습니다.', '감정 기복이 클 수 있어요. 명상과 휴식을 권장합니다.'],
  ['봄기운과 함께 도약의 시기. 적극적으로 나아가세요.', '직업·학업에서 긍정적 신호. 집중력을 발휘하세요.', '인내가 필요한 달. 성급한 결정은 피하세요.'],
  ['재물운이 상승하는 달. 기회를 놓치지 마세요.', '건강 관리에 특히 신경 쓰세요. 과로는 금물입니다.', '창의적 아이디어가 빛나는 시기. 표현을 두려워 말아요.'],
  ['인간관계에서 뜻밖의 도움을 받을 수 있어요.', '애정운이 강한 달. 감정 표현을 솔직하게 하세요.', '사업·직장에서 중요한 결정이 있을 수 있어요.'],
  ['전반적으로 안정적인 달. 기존 계획을 꾸준히 실천하세요.', '여행·이동이 길한 시기. 새로운 환경을 경험해보세요.', '학습과 자기계발에 집중하기 좋은 달입니다.'],
  ['재물·사업에서 좋은 성과가 예상됩니다.', '건강에 주의가 필요한 시기. 충분한 수면을 취하세요.', '대인관계에서 갈등이 생길 수 있어요. 소통이 중요합니다.'],
  ['강한 에너지의 달. 목표를 향해 집중 달려가세요.', '예상치 못한 변수가 생길 수 있어요. 유연하게 대처하세요.', '애정운·가정운이 따뜻한 달입니다.'],
  ['결실의 계절. 그동안의 노력이 빛을 발합니다.', '새로운 프로젝트나 관계 시작에 좋은 시기입니다.', '재정 점검을 해보는 것이 좋습니다.'],
  ['이동·변화·이직 등 큰 변화가 예상됩니다.', '인간관계에서 중요한 결정이 필요할 수 있어요.', '학업·자격증 등 성취 운이 좋은 달입니다.'],
  ['재물 지출이 늘어날 수 있어요. 계획적인 지출을 권장합니다.', '가족·가정에 집중하는 달. 소중한 사람과 시간을 보내세요.', '창작·예술 활동에서 영감이 넘치는 시기입니다.'],
  ['한 해를 마무리하는 달. 감사한 마음으로 정리하세요.', '새해를 위한 준비에 집중하는 것이 좋습니다.', '건강 점검과 휴식이 필요한 시기입니다.'],
];

const LUCKY_COLORS = ['빨강', '주황', '노랑', '초록', '청록', '파랑', '보라', '분홍', '흰색', '검정', '금색', '은색'];

function buildMonthData(birthDate: string, year: number, month: number): MonthData {
  const [y, m, d] = birthDate.split('-').map(Number);
  const baseSeed = ((y * 31 + m) * 17 + d * 7) ^ (year * 1000 + month);
  const rand = seededRand(baseSeed);

  const overall = Math.floor(rand() * 36) + 60;
  const money   = Math.floor(rand() * 36) + 60;
  const love    = Math.floor(rand() * 36) + 60;
  const health  = Math.floor(rand() * 36) + 60;
  const career  = Math.floor(rand() * 36) + 60;
  const social  = Math.floor(rand() * 36) + 60;

  const advicePool = MONTH_ADVICES[month - 1];
  const adviceIdx  = Math.floor(rand() * advicePool.length);
  const luckyIdx   = Math.floor(rand() * LUCKY_COLORS.length);

  return {
    month,
    monthKo: MONTHS_KO[month - 1],
    overall,
    money,
    love,
    health,
    career,
    social,
    advice: advicePool[adviceIdx],
    lucky: LUCKY_COLORS[luckyIdx],
  };
}

function getScoreColor(score: number): string {
  if (score >= 85) return 'text-primary-gold';
  if (score >= 75) return 'text-emerald-400';
  if (score >= 65) return 'text-indigo-300';
  return 'text-gray-400';
}

function getScoreBg(score: number): string {
  if (score >= 85) return 'bg-primary-gold/30 border-primary-gold/60';
  if (score >= 75) return 'bg-emerald-500/20 border-emerald-500/50';
  if (score >= 65) return 'bg-indigo-500/20 border-indigo-500/40';
  return 'bg-white/5 border-white/15';
}

function getScoreLabel(score: number): string {
  if (score >= 85) return '대길';
  if (score >= 75) return '길';
  if (score >= 65) return '보통';
  return '주의';
}

function ScoreBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${value}%` }}
        ></div>
      </div>
      <span className={`text-xs font-bold w-6 text-right ${getScoreColor(value)}`}>{value}</span>
    </div>
  );
}

export default function MonthlyFortuneCalendar({ birthDate, birthTime: _birthTime, gender: _gender }: MonthlyFortuneCalendarProps) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(currentMonth);

  const monthsData = useMemo<MonthData[]>(
    () => Array.from({ length: 12 }, (_, i) => buildMonthData(birthDate, selectedYear, i + 1)),
    [birthDate, selectedYear],
  );

  const detailData = selectedMonth ? monthsData[selectedMonth - 1] : null;

  const categories = [
    { key: 'money',  label: '재물운', icon: 'ri-money-dollar-circle-line', barColor: 'bg-yellow-400' },
    { key: 'love',   label: '애정운', icon: 'ri-heart-3-line',              barColor: 'bg-pink-400'   },
    { key: 'health', label: '건강운', icon: 'ri-heart-pulse-line',          barColor: 'bg-green-400'  },
    { key: 'career', label: '직업운', icon: 'ri-briefcase-4-line',          barColor: 'bg-amber-400'  },
    { key: 'social', label: '대인운', icon: 'ri-group-line',                barColor: 'bg-indigo-400' },
  ] as const;

  return (
    <div className="w-full">
      {/* Year Switcher */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 p-1 bg-white/5 rounded-full border border-white/15">
          {[currentYear, currentYear + 1].map((yr) => (
            <button
              key={yr}
              onClick={() => { setSelectedYear(yr); setSelectedMonth(null); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                selectedYear === yr
                  ? 'bg-gradient-to-r from-primary-gold to-amber-500 text-primary-dark shadow-lg'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              {yr}년
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-indigo-400">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary-gold/80 inline-block"></span>대길</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block"></span>길</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400 inline-block"></span>보통</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white/30 inline-block"></span>주의</span>
        </div>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
        {monthsData.map((md) => {
          const isSelected = selectedMonth === md.month;
          const isToday = selectedYear === currentYear && md.month === currentMonth;
          return (
            <button
              key={md.month}
              onClick={() => setSelectedMonth(isSelected ? null : md.month)}
              className={`relative rounded-2xl p-3 sm:p-4 border-2 transition-all duration-200 cursor-pointer text-left ${
                isSelected
                  ? 'border-primary-gold bg-primary-gold/15 scale-[1.03]'
                  : `${getScoreBg(md.overall)} hover:scale-[1.02]`
              }`}
            >
              {isToday && (
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary-gold animate-pulse"></span>
              )}
              <p className="text-white font-bold text-sm mb-1">{md.monthKo}</p>
              <p className={`text-lg font-black ${getScoreColor(md.overall)}`}>{md.overall}</p>
              <p className={`text-[10px] font-semibold mt-0.5 ${getScoreColor(md.overall)}`}>
                {getScoreLabel(md.overall)}
              </p>
              {/* Mini score bars */}
              <div className="mt-2 space-y-1">
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-yellow-400/70 rounded-full" style={{ width: `${md.money}%` }}></div>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-pink-400/70 rounded-full" style={{ width: `${md.love}%` }}></div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail Panel */}
      {detailData && (
        <div className="bg-gradient-to-br from-white/8 to-white/3 rounded-2xl p-5 sm:p-7 border border-primary-gold/30 animate-fade-in">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-white font-black text-xl sm:text-2xl">
                {selectedYear}년 {detailData.monthKo}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-2xl sm:text-3xl font-black ${getScoreColor(detailData.overall)}`}>
                  {detailData.overall}점
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getScoreBg(detailData.overall)} ${getScoreColor(detailData.overall)}`}>
                  {getScoreLabel(detailData.overall)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-indigo-400 text-xs mb-1">이달의 행운 색상</p>
              <p className="text-primary-gold text-sm font-bold">{detailData.lucky}</p>
            </div>
          </div>

          {/* Category Scores */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {categories.map((cat) => (
              <div key={cat.key} className="bg-white/5 rounded-xl p-3 border border-white/8">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm ${getScoreColor(detailData[cat.key])}`}></i>
                  </div>
                  <span className="text-indigo-300 text-xs font-medium">{cat.label}</span>
                  <span className={`ml-auto text-xs font-bold ${getScoreColor(detailData[cat.key])}`}>
                    {getScoreLabel(detailData[cat.key])}
                  </span>
                </div>
                <ScoreBar value={detailData[cat.key]} color={cat.barColor} />
              </div>
            ))}
          </div>

          {/* Advice */}
          <div className="flex items-start gap-3 p-4 bg-primary-gold/10 rounded-xl border border-primary-gold/25">
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
              <i className="ri-sparkling-2-fill text-primary-gold text-lg"></i>
            </div>
            <p className="text-indigo-100 text-sm leading-relaxed">{detailData.advice}</p>
          </div>
        </div>
      )}
    </div>
  );
}
